import { useCallback, useEffect, useMemo, useState } from "react";
import type { Lang } from "../types.ts";
import type { SyncDoc, SyncedProgress } from "../sync/types.ts";
import { emptyDoc, RECENT_LIMIT } from "../sync/types.ts";

const KEY_SETTINGS = "t24.settings";
const KEY_DOC = "t24.doc";
/** Pre-sync storage keys, migrated once on first load. */
const LEGACY_PROGRESS = "t24.progress";
const LEGACY_MARKED = "t24.marked";

export interface Settings {
  /** Language the question is shown in first. */
  primary: Lang;
  /** Shown underneath the primary, or null for a single-language view. */
  secondary: Lang | null;
  /** Reveal the explanation automatically after answering in practice. */
  autoExplain: boolean;
  fontScale: number;
}

const DEFAULT_SETTINGS: Settings = {
  primary: "en",
  secondary: "zh",
  autoExplain: true,
  fontScale: 1,
};

function readJson<T>(key: string): unknown | null {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function useSettings() {
  const [settings, setSettings] = useState<Settings>(() => {
    const stored = readJson(KEY_SETTINGS);
    return stored && typeof stored === "object"
      ? { ...DEFAULT_SETTINGS, ...(stored as Partial<Settings>) }
      : DEFAULT_SETTINGS;
  });

  useEffect(() => {
    localStorage.setItem(KEY_SETTINGS, JSON.stringify(settings));
    document.documentElement.style.setProperty("--font-scale", String(settings.fontScale));
  }, [settings]);

  const update = useCallback(
    (patch: Partial<Settings>) => setSettings((s) => ({ ...s, ...patch })),
    []
  );

  /** Swaps primary and secondary; a no-op when there is no secondary. */
  const swapLanguages = useCallback(
    () =>
      setSettings((s) =>
        s.secondary ? { ...s, primary: s.secondary, secondary: s.primary } : s
      ),
    []
  );

  return { settings, update, swapLanguages };
}

export type QuestionProgress = SyncedProgress;
export type ProgressMap = Record<string, SyncedProgress>;

/** Moves pre-sync localStorage data into the timestamped document shape. */
function migrateLegacy(): SyncDoc | null {
  const oldProgress = readJson(LEGACY_PROGRESS);
  const oldMarked = readJson(LEGACY_MARKED);
  if (!oldProgress && !oldMarked) return null;

  const doc = emptyDoc();
  const stamp = Date.now();
  if (oldProgress && typeof oldProgress === "object") {
    for (const [id, value] of Object.entries(oldProgress as Record<string, any>)) {
      doc.progress[id] = {
        seen: Number(value?.seen) || 0,
        correct: Number(value?.correct) || 0,
        wrong: Number(value?.wrong) || 0,
        lastCorrect: value?.lastCorrect ?? null,
        updatedAt: stamp,
      };
    }
  }
  if (Array.isArray(oldMarked)) {
    for (const id of oldMarked) doc.marked[String(id)] = { marked: true, updatedAt: stamp };
  }
  doc.updatedAt = stamp;

  localStorage.removeItem(LEGACY_PROGRESS);
  localStorage.removeItem(LEGACY_MARKED);
  return doc;
}

function loadDoc(): SyncDoc {
  const stored = readJson(KEY_DOC);
  if (stored && typeof stored === "object") {
    const doc = stored as Partial<SyncDoc>;
    return {
      progress: doc.progress ?? {},
      marked: doc.marked ?? {},
      updatedAt: doc.updatedAt ?? 0,
    };
  }
  return migrateLegacy() ?? emptyDoc();
}

/**
 * The user's study record. Held as one document so it can be merged with the
 * server copy wholesale; components still see the plain progress/marked views.
 */
export function useStudyDoc() {
  const [doc, setDoc] = useState<SyncDoc>(loadDoc);

  useEffect(() => {
    localStorage.setItem(KEY_DOC, JSON.stringify(doc));
  }, [doc]);

  const record = useCallback((questionId: number, correct: boolean) => {
    setDoc((d) => {
      const key = String(questionId);
      const prev = d.progress[key];
      const now = Date.now();
      return {
        ...d,
        progress: {
          ...d.progress,
          [key]: {
            seen: (prev?.seen ?? 0) + 1,
            correct: (prev?.correct ?? 0) + (correct ? 1 : 0),
            wrong: (prev?.wrong ?? 0) + (correct ? 0 : 1),
            lastCorrect: correct,
            recent: (correct ? "1" : "0").concat(prev?.recent ?? "").slice(0, RECENT_LIMIT),
            updatedAt: now,
          },
        },
        updatedAt: now,
      };
    });
  }, []);

  const toggleMark = useCallback((questionId: number) => {
    setDoc((d) => {
      const key = String(questionId);
      const now = Date.now();
      return {
        ...d,
        marked: { ...d.marked, [key]: { marked: !d.marked[key]?.marked, updatedAt: now } },
        updatedAt: now,
      };
    });
  }, []);

  /** Ids currently starred, in ascending question order. */
  const marked = useMemo(
    () =>
      Object.entries(doc.marked)
        .filter(([, m]) => m.marked)
        .map(([id]) => Number(id))
        .sort((a, b) => a - b),
    [doc.marked]
  );

  const isMarked = useCallback(
    (questionId: number) => Boolean(doc.marked[String(questionId)]?.marked),
    [doc.marked]
  );

  /**
   * Clears progress locally. Entries are kept as zeroed, freshly stamped rows
   * so the reset wins the next merge instead of being resurrected from another
   * device's copy.
   */
  const reset = useCallback(() => {
    setDoc((d) => {
      const now = Date.now();
      const progress: SyncDoc["progress"] = {};
      for (const key of Object.keys(d.progress)) {
        progress[key] = { seen: 0, correct: 0, wrong: 0, lastCorrect: null, updatedAt: now };
      }
      const marked: SyncDoc["marked"] = {};
      for (const key of Object.keys(d.marked)) {
        marked[key] = { marked: false, updatedAt: now };
      }
      return { progress, marked, updatedAt: now };
    });
  }, []);

  return {
    doc,
    setDoc,
    progress: doc.progress as ProgressMap,
    record,
    marked,
    toggleMark,
    isMarked,
    reset,
  };
}
